'use client'

import type { ColumnDef } from '@tanstack/react-table'
import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { ClipboardIcon, Pencil } from 'lucide-react'

import type { RouterOutputs } from '@package/api'
import { Avatar, AvatarFallback, AvatarImage, Button, Checkbox, toast } from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import { calculateGradeBasedOnXp } from '@helper/utils/business-logic'
import { formatDate, getInitials } from '@helper/utils/format'
import cn from '@config/tailwind/cn'

import type { ScoreboardType } from '../index'
import { useSharedTRPC } from '../../../../context'
import { GiveGradeModal } from './give-grade-modal'
import { ReviewQuizModal } from './review-quiz-modal'
import { ShowWorkModal } from './show-work-modal'

type Data = RouterOutputs['lesson']['getScoreboardData']['items'][number]

function MarkCompleteButton({
  learnProgressId,
  onSuccess,
}: {
  learnProgressId: string
  onSuccess?: () => void | Promise<void>
}) {
  const trpc = useSharedTRPC()

  const mutation = useMutation(
    trpc.lesson.editLearnProgress.mutationOptions({
      onSuccess: async () => {
        toast.success('Berhasil ditandai selesai')
        await onSuccess?.()
      },
      onError: () => {
        toast.error('Gagal menandai selesai')
      },
    }),
  )

  return (
    <Button
      variant='ghost'
      size='sm'
      className='srd:p-0 srd:w-fit srd:h-fit'
      loading={mutation.isPending}
      disabled={mutation.isPending}
      onClick={() =>
        mutation.mutate({
          learnProgressIds: [learnProgressId],
          data: { status: 'COMPLETED' },
        })
      }
    >
      Tandai selesai?
    </Button>
  )
}

interface UseColumnsProps {
  p: ScoreboardType
  lessonId: string | null
  isInClass: boolean
  isQuiz: boolean
  onInvalidateScoreboard?: () => void | Promise<void>
}

export const useColumns = ({
  p,
  lessonId,
  isInClass,
  isQuiz,
  onInvalidateScoreboard,
}: UseColumnsProps): ColumnDef<Data>[] => {
  const [giveGradeModalOpenId, setGiveGradeModalOpenId] = useState<string | null>(null)
  const [showWorkModalId, setShowWorkModalId] = useState<string | null>(null)
  const [reviewQuizModalId, setReviewQuizModalId] = useState<string | null>(null)

  const selectColumn: ColumnDef<Data> = {
    id: 'select',
    enableHiding: false,
    enableSorting: false,
    header: ({ table }) => (
      <Checkbox
        checked={table.getIsAllPageRowsSelected()}
        indeterminate={table.getIsSomePageRowsSelected()}
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
      />
    ),
    cell: ({ row }) => (
      <Checkbox checked={row.getIsSelected()} onCheckedChange={(value) => row.toggleSelected(!!value)} />
    ),
  }

  const coreColumns: ColumnDef<Data>[] = [
    {
      id: 'name',
      accessorKey: 'name',
      accessorFn: (row) => row.child.name,
      enableHiding: false,
      enableSorting: false,
      header: 'Nama',
      cell: ({ row }) => (
        <div className='srd:flex srd:items-center srd:gap-x-2 srd:flex-nowrap srd:shrink-0'>
          <Avatar className='srd:size-10'>
            <AvatarImage src={row.original.child.image ?? undefined} />
            <AvatarFallback>{getInitials(row.original.child.name)}</AvatarFallback>
          </Avatar>

          <div className='srd:flex srd:flex-col srd:gap-y-1 srd:shrink-0'>
            <p className={cn(typographyVariants({ variant: 'body-lg' }))}>{row.original.child.name}</p>

            {p.type === 'challenge' && row.original.child.organization && (
              <p className={cn(typographyVariants({ variant: 'label-sm' }), 'srd:text-grey-100')}>
                {`${row.original.child.organization.name} ${row.original.child.team ? `(${row.original.child.team.name})` : ''}`.trim()}
              </p>
            )}

            {p.type === 'course' && row.original.child.team && (
              <p className={cn(typographyVariants({ variant: 'label-sm' }), 'srd:text-grey-100')}>
                {row.original.child.team.name}
              </p>
            )}
          </div>
        </div>
      ),
    },
    {
      id: 'started-at',
      enableHiding: false,
      enableSorting: false,
      header: 'Mulai',
      cell: ({ row }) => {
        const lp = row.original.learnProgress
        if (!lp) {
          return (
            <span className={cn(typographyVariants({ variant: 'body-md' }), 'srd:text-grey-100')}>Belum mulai</span>
          )
        }
        const displayDate = lp.startedAt ?? lp.createdAt
        return (
          <div className={cn(typographyVariants({ variant: 'body-md' }), 'srd:text-nowrap')}>
            {formatDate(displayDate, { dateStyle: 'long', timeStyle: 'short' })}
          </div>
        )
      },
    },
    {
      id: 'ended-at',
      enableHiding: false,
      enableSorting: false,
      header: 'Selesai',
      cell: ({ row }) => {
        const lp = row.original.learnProgress

        if (lp?.endedAt) {
          return (
            <div className={cn(typographyVariants({ variant: 'body-md' }), 'srd:text-nowrap')}>
              {formatDate(lp.endedAt, { dateStyle: 'long', timeStyle: 'short' })}
            </div>
          )
        }

        if (isInClass && lp) {
          return <MarkCompleteButton learnProgressId={lp.id} onSuccess={onInvalidateScoreboard} />
        }

        return <span className={cn(typographyVariants({ variant: 'body-md' }), 'srd:text-grey-100')}>-</span>
      },
    },
    {
      id: 'points',
      enableHiding: false,
      enableSorting: false,
      header: 'Poin',
      cell: ({ row }) => {
        const lp = row.original.learnProgress

        if (isInClass) {
          const isGraded = (lp?.earnedXps.length ?? 0) > 0
          const canGrade = !!lp && lp.status === 'COMPLETED' && !isGraded

          if (isGraded && lp?.lesson) {
            const { grade } = calculateGradeBasedOnXp({
              lessons: [lp.lesson],
              learnProgresses: [lp],
            })

            return (
              <div className='srd:flex srd:items-center srd:flex-nowrap srd:shrink-0'>
                <span className={cn(typographyVariants({ variant: 'body-lg' }))}>{Math.round(grade)}</span>

                <Button variant='ghost' size='sm' onClick={() => setGiveGradeModalOpenId(row.original.id)}>
                  <Pencil className='srd:size-4 srd:text-grey-100' />
                </Button>
              </div>
            )
          }

          return (
            <Button
              variant='ghost'
              size='sm'
              className='srd:p-0 srd:w-fit srd:h-fit'
              disabled={!canGrade}
              onClick={() => {
                if (canGrade) setGiveGradeModalOpenId(row.original.id)
              }}
            >
              <Pencil className='srd:size-4 srd:mr-2' />
              Perlu nilai
            </Button>
          )
        }

        // Non-in-class
        const totalXp = (lp?.earnedXps ?? []).reduce((sum, x) => sum + x.xpEarned, 0)
        if (!lp || lp.earnedXps.length === 0) {
          return (
            <span className={cn(typographyVariants({ variant: 'body-md' }), 'srd:text-grey-100')}>Belum ada poin</span>
          )
        }
        return <span className={cn(typographyVariants({ variant: 'body-lg' }))}>{totalXp}</span>
      },
    },
    {
      id: 'actions',
      enableHiding: false,
      cell: ({ row }) => {
        const lp = row.original.learnProgress
        if (!isInClass && !isQuiz) return null

        if (isInClass) {
          const hasWorkShown = !!lp && (lp.childSubmissions.length > 0 || lp.mainUserAttachments.length > 0)

          return (
            <div className='srd:flex srd:items-center srd:gap-x-2'>
              <Button
                variant='ghost'
                size='sm'
                className='srd:p-0 srd:w-fit srd:h-fit srd:text-neutral-black'
                disabled={!hasWorkShown}
                onClick={() => {
                  if (hasWorkShown) setShowWorkModalId(row.original.id)
                }}
              >
                <ClipboardIcon className='srd:size-4 srd:mr-2' />
                Hasil kerja
              </Button>

              <GiveGradeModal
                open={giveGradeModalOpenId === row.original.id}
                onOpenChange={(open) => {
                  if (!open) setGiveGradeModalOpenId(null)
                }}
                childProfile={row.original.child}
                data={row.original}
                onInvalidateScoreboard={onInvalidateScoreboard}
                {...p}
              />

              <ShowWorkModal
                open={showWorkModalId === row.original.id}
                onOpenChange={(open) => {
                  if (!open) setShowWorkModalId(null)
                }}
                childProfile={row.original.child}
                data={row.original}
                {...p}
              />
            </div>
          )
        }

        const isCompleted = lp?.status === 'COMPLETED'

        return (
          <div className='srd:flex srd:items-center srd:gap-x-2'>
            <Button
              variant='ghost'
              size='sm'
              className='srd:p-0 srd:w-fit srd:h-fit srd:text-neutral-black'
              disabled={!isCompleted || !lessonId}
              onClick={() => {
                if (isCompleted && lessonId) setReviewQuizModalId(row.original.id)
              }}
            >
              Tinjau kuis
            </Button>

            {isCompleted && lessonId && (
              <ReviewQuizModal
                open={reviewQuizModalId === row.original.id}
                onOpenChange={(open) => {
                  if (!open) setReviewQuizModalId(null)
                }}
                lessonId={lessonId}
                learnEnrollmentId={row.original.id}
                childProfile={row.original.child}
              />
            )}
          </div>
        )
      },
    },
  ]

  return isInClass ? [selectColumn, ...coreColumns] : coreColumns
}
